import React, { useState } from 'react';
import { useFitStore } from '../../store/useFitStore';
import type { Appointment, AppointmentStatus } from '../../store/types';
import { buildVcalendar } from '../../lib/ics';
import { googleCalendarUrl } from '../../lib/gcal';

const STATUSES: { key: AppointmentStatus; label: string }[] = [
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'completed', label: 'Completed' },
  { key: 'no_show', label: 'No-show' },
  { key: 'canceled', label: 'Canceled' },
];

const RECURRENCE: { key: string; label: string; rrule?: string }[] = [
  { key: 'none', label: 'Does not repeat', rrule: undefined },
  { key: 'daily', label: 'Daily', rrule: 'FREQ=DAILY' },
  { key: 'weekly', label: 'Weekly', rrule: 'FREQ=WEEKLY' },
  { key: 'biweekly', label: 'Every 2 weeks', rrule: 'FREQ=WEEKLY;INTERVAL=2' },
];
const presetFromRrule = (rrule?: string): string =>
  RECURRENCE.find((r) => r.rrule === rrule)?.key ?? (rrule ? 'weekly' : 'none');

type AttendeeKind = 'client' | 'lead' | 'other';

// Local wall-clock <-> ISO helpers. The editor works in the browser's zone; the
// appointment's `timezone` is (re)captured on save, so recurrence stays anchored
// to the wall-clock time the coach entered.
const toDateVal = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const toTimeVal = (iso: string): string => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const combine = (date: string, time: string): string => new Date(`${date}T${time}`).toISOString();

interface Props {
  appt: Appointment;
  isNew: boolean;
  occurrenceStartIso?: string; // set when opened from a specific occurrence of a series
  onClose: () => void;
}

const AppointmentEditor: React.FC<Props> = ({ appt, isNew, occurrenceStartIso, onClose }) => {
  const { user, clients, leads, upsertAppointment, deleteAppointment, cancelOccurrence, showToast } = useFitStore();

  const initialKind: AttendeeKind = appt.clientId ? 'client' : appt.leadId ? 'lead' : 'other';
  const [kind, setKind] = useState<AttendeeKind>(initialKind);
  const [clientId, setClientId] = useState(appt.clientId || '');
  const [leadId, setLeadId] = useState(appt.leadId || '');
  const [attendeeName, setAttendeeName] = useState(appt.attendeeName || '');
  const [attendeeEmail, setAttendeeEmail] = useState(appt.attendeeEmail || '');

  const [title, setTitle] = useState(appt.title);
  const [date, setDate] = useState(toDateVal(appt.startsAt));
  const [startTime, setStartTime] = useState(toTimeVal(appt.startsAt));
  const [endTime, setEndTime] = useState(toTimeVal(appt.endsAt));
  const [preset, setPreset] = useState(presetFromRrule(appt.rrule));
  const [location, setLocation] = useState(appt.location || '');
  const [notes, setNotes] = useState(appt.notes || '');
  const [status, setStatus] = useState<AppointmentStatus>(appt.status);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Resolve the attendee snapshot from the selected client/lead, or the free-text inputs.
  const resolveAttendee = (): { name?: string; email?: string } => {
    if (kind === 'client') {
      const c = clients.find((x) => x.id === clientId);
      return { name: c?.name, email: c?.email || undefined };
    }
    if (kind === 'lead') {
      const l = leads.find((x) => x.id === leadId);
      return { name: l?.name, email: l?.email || undefined };
    }
    return { name: attendeeName.trim() || undefined, email: attendeeEmail.trim() || undefined };
  };

  const build = (): Appointment | null => {
    if (!user) return null;
    const startsAt = combine(date, startTime);
    const endsAt = combine(date, endTime);
    if (new Date(endsAt) <= new Date(startsAt)) {
      showToast('End time must be after the start', 'error');
      return null;
    }
    const att = resolveAttendee();
    if (!title.trim() && !att.name) {
      showToast('Add a title or pick who it’s with', 'error');
      return null;
    }
    return {
      ...appt,
      coachId: user.id,
      clientId: kind === 'client' ? clientId || undefined : undefined,
      leadId: kind === 'lead' ? leadId || undefined : undefined,
      attendeeName: att.name,
      attendeeEmail: att.email,
      title: title.trim(),
      location: location.trim() || undefined,
      notes: notes.trim() || undefined,
      startsAt,
      endsAt,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      status,
      rrule: RECURRENCE.find((r) => r.key === preset)?.rrule,
    };
  };

  const save = () => {
    const next = build();
    if (!next) return;
    upsertAppointment(next);
    showToast(isNew ? 'Appointment added' : 'Appointment saved');
    onClose();
  };

  const download = () => {
    const next = build();
    if (!next) return;
    const ics = buildVcalendar([next]);
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(next.title || 'appointment').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Open Google Calendar prefilled, client as guest — Google sends the invite,
  // the reminders, and tracks the RSVP. Saves the appointment first so FitFlex
  // and Google agree on what was scheduled.
  const inviteViaGoogle = () => {
    const next = build();
    if (!next) return;
    upsertAppointment(next);
    if (!next.attendeeEmail) showToast('No email on file — opening Google without a guest');
    window.open(googleCalendarUrl(next), '_blank', 'noopener');
    onClose();
  };

  const canCancelOccurrence = Boolean(occurrenceStartIso && appt.rrule && !isNew);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="card flex flex-col" style={{ maxHeight: '88vh' }}>
          <div className="flex items-start justify-between gap-3 mb-1">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Appointment title"
              className="bg-transparent text-xl font-bold tracking-tight outline-none flex-1 min-w-0 border-b border-transparent focus:border-fit-line-strong pb-0.5"
            />
            <button onClick={onClose} className="text-fit-text-secondary hover:text-white text-lg leading-none shrink-0">×</button>
          </div>
          <p className="mono-label mb-4">{isNew ? 'New appointment' : 'Appointment'}</p>

          <div className="overflow-y-auto no-scrollbar flex-1 pr-1">
            {/* attendee */}
            <p className="mono-label mb-1.5">With</p>
            <div className="flex items-center gap-1 mb-3">
              {(['client', 'lead', 'other'] as AttendeeKind[]).map((k) => (
                <button
                  key={k}
                  onClick={() => setKind(k)}
                  className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors ${
                    kind === k ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
                  }`}
                >
                  {k === 'other' ? 'Other' : k}
                </button>
              ))}
            </div>
            {kind === 'client' && (
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="input mb-4">
                <option value="">Select a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            {kind === 'lead' && (
              <select value={leadId} onChange={(e) => setLeadId(e.target.value)} className="input mb-4">
                <option value="">Select a lead…</option>
                {leads.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            {kind === 'other' && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div><p className="mono-label mb-1.5">Name</p>
                  <input value={attendeeName} onChange={(e) => setAttendeeName(e.target.value)} placeholder="—" className="input" /></div>
                <div><p className="mono-label mb-1.5">Email</p>
                  <input value={attendeeEmail} onChange={(e) => setAttendeeEmail(e.target.value)} placeholder="—" className="input" /></div>
              </div>
            )}

            {/* when */}
            <div className="grid grid-cols-3 gap-3">
              <div className="mb-4"><p className="mono-label mb-1.5">Date</p>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input" /></div>
              <div className="mb-4"><p className="mono-label mb-1.5">Start</p>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input" /></div>
              <div className="mb-4"><p className="mono-label mb-1.5">End</p>
                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="input" /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="mb-4"><p className="mono-label mb-1.5">Repeats</p>
                <select value={preset} onChange={(e) => setPreset(e.target.value)} className="input">
                  {RECURRENCE.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select></div>
              <div className="mb-4"><p className="mono-label mb-1.5">Location</p>
                <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Zoom, studio, address…" className="input" /></div>
            </div>

            {!isNew && (
              <div className="mb-4">
                <p className="mono-label mb-1.5">Status</p>
                <div className="flex items-center gap-1 flex-wrap">
                  {STATUSES.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setStatus(s.key)}
                      className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors ${
                        status === s.key ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-2"><p className="mono-label mb-1.5">Notes</p>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Agenda, what to prep, links…" className="input" style={{ minHeight: 72 }} /></div>

            {canCancelOccurrence && (
              <button
                onClick={() => { cancelOccurrence(appt.id, occurrenceStartIso!); showToast('Occurrence canceled'); onClose(); }}
                className="text-[12px] text-fit-muted hover:text-white underline underline-offset-2"
              >
                Cancel just this occurrence
              </button>
            )}
          </div>

          {/* footer */}
          <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-fit-line">
            {!isNew ? (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-fit-text-secondary">Delete{appt.rrule ? ' series' : ''}?</span>
                  <button onClick={() => { deleteAppointment(appt.id); showToast('Appointment deleted'); onClose(); }} className="text-[12px] font-semibold text-white underline underline-offset-2">Yes</button>
                  <button onClick={() => setConfirmDelete(false)} className="text-[12px] text-fit-muted hover:text-white">Cancel</button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(true)} className="text-[12px] text-fit-muted hover:text-white">Delete</button>
              )
            ) : <span />}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button onClick={download} className="btn btn-glass btn-sm" title="Download an .ics file (Apple / Outlook)">.ics ↓</button>
              <button
                onClick={inviteViaGoogle}
                className="btn btn-glass btn-sm"
                title="Saves, then opens Google Calendar prefilled with the client as a guest — Google emails the invite and reminders"
              >
                📅 Invite via Google
              </button>
              <button onClick={save} className="btn btn-primary btn-sm">{isNew ? 'Add appointment' : 'Save'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentEditor;
